import { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { db } from "../../firebase";
import { collection, query, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";

export default function ConversationDetailDialogMobile({ open, onClose, agentId, conversationId }) {
  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !agentId || !conversationId) return;
    setLoading(true);
    let unsub;
    (async () => {
      try {
        const convRef = doc(db, "messages", `agent_${agentId}`, "conversations", conversationId);
        const metaSnap = await getDoc(convRef);
        if (metaSnap.exists()) setMeta(metaSnap.data());
        const eventsRef = collection(convRef, "events");
        const q = query(eventsRef, orderBy("timestamp", "asc"));
        unsub = onSnapshot(q, (snap) => {
          setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        });
      } catch (e) {
        setLoading(false);
      }
    })();
    return () => unsub && unsub();
  }, [open, agentId, conversationId]);

  const fmt = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "");

  // Basic highlighter for phones and probable names (similar spirit to desktop)
  const renderWithHighlights = (text) => {
    if (!text) return "";
    const matches = [];
    const phoneRe = /(\+?\d[\d\s().-]{6,}?\d)/g;
    let m;
    while ((m = phoneRe.exec(text))) {
      const raw = m[1];
      const start = m.index;
      const len = raw.length;
      const normalized = raw.replace(/[^\d+]/g, "");
      const digits = (normalized.match(/\d/g) || []).length;
      if (digits >= 7 && digits <= 15) {
        matches.push({ start, end: start + len, type: "phone", raw, normalized, prio: 2 });
      }
    }
    const capPair = /\b([A-Z][A-Za-z'\-]{1,24})(?:\s+([A-Z][A-Za-z'\-]{1,24}))\b/g;
    let mh;
    while ((mh = capPair.exec(text))) {
      const g1 = mh[1];
      const g2 = mh[2];
      const full = `${g1} ${g2}`;
      const start = mh.index;
      const end = start + full.length;
      matches.push({ start, end, type: "name", raw: full, prio: 1 });
    }
    if (matches.length === 0) return text;
    matches.sort((a, b) => (b.prio - a.prio) || (a.start - b.start));
    const taken = [];
    for (const it of matches) {
      const overlaps = taken.some((t) => !(it.end <= t.start || it.start >= t.end));
      if (!overlaps) taken.push(it);
    }
    taken.sort((a, b) => a.start - b.start);
    const out = [];
    let last = 0;
    let idx = 0;
    for (const t of taken) {
      if (t.start > last) out.push(text.slice(last, t.start));
      if (t.type === "phone") {
        const normalized = t.normalized;
        const tel = normalized.startsWith("+") ? normalized : `+${normalized}`;
        out.push(
          <a
            key={`ph-${idx++}-${t.start}`}
            href={`tel:${tel}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(255,64,129,0.24)",
              border: "1px solid #FF4081",
              borderRadius: 9999,
              padding: "2px 8px",
              margin: "0 2px",
              color: "inherit",
              textDecoration: "none",
              lineHeight: 1.4,
              fontWeight: 700,
            }}
            title={tel}
          >
            {t.raw}
          </a>
        );
      } else {
        out.push(
          <span
            key={`nm-${idx++}-${t.start}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(33,150,243,0.22)",
              border: "1px solid #2196F3",
              borderRadius: 9999,
              padding: "2px 8px",
              margin: "0 2px",
              lineHeight: 1.4,
              fontWeight: 700,
            }}
          >
            {t.raw}
          </span>
        );
      }
      last = t.end;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ display: "flex", alignItems: "center", p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 700 }}>Conversation</Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1, height: "calc(100% - 56px)", overflow: "auto" }}>
        {meta && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip size="small" label={`Interactions: ${meta.interactions ?? 0}`} />
            {meta?.userHints?.email && <Chip size="small" label={`Email: ${meta.userHints.email}`} />}
            {meta?.updatedAt?.seconds && <Chip size="small" label={`Updated: ${fmt(meta.updatedAt)}`} />}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No messages in this conversation.
          </Typography>
        ) : (
          events.map((e) => (
            <Box
              key={e.id}
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor:
                  e.from === "user"
                    ? "background.default"
                    : e.from === "bot"
                    ? "action.hover"
                    : "background.paper",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }} color={e.from === "user" ? "primary" : e.from === "bot" ? "secondary" : "text.secondary"}>
                  {e.from}
                </Typography>
                <Typography variant="caption" color="text.secondary">{fmt(e.timestamp)}</Typography>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{renderWithHighlights(e.body || "")}</Typography>
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
}
